import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Button, PortalPanel, PortalPanelBody, Stack, Typography } from "../../design-system";
import { pendingShellCopy } from "../../routing/reload-failure-copy";
import { exposureContent } from "./exposure-content";
import {
  findOnFileScreen,
  humanizeFieldId,
  isOnFilePanelForcedOpen,
  onFileAttributeText,
  onFileItemFlagWord,
  onFileItemHeading,
} from "./on-file-render";
import type { OnFileRenderContext } from "./on-file-render";
import type { RepeatItemAnswer } from "./exposure-answers";
import type { SectionOnFileSpec } from "./on-file-fields";
import type { OnFileScreenItem } from "../../state/on-file-port";

export interface OnFilePanelProps {
  readonly id: string;
  readonly spec: SectionOnFileSpec;
  readonly onFile: OnFileRenderContext | undefined;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  /** The repeat items of `spec.soldSource`'s question (H2 `soldSource`), read for the
   * Sold/Replaced/Removed flag beside each record (AC-2). Omitted where the question flags
   * nothing. */
  readonly flagItems?: readonly RepeatItemAnswer[];
  /** Whether a SIBLING `OnFilePicker` for the SAME question is actually
   * mounted, and so already renders the failed-read retry control -- the
   * panel then states the failure without a second, competing retry
   * control (SC 2.4.3 names ONE retry control per failed read, not two
   * racing for focus). Default `false`. */
  readonly retryOwnedElsewhere?: boolean;
  /** Whether this question's spec declares a picker. The picker opens
   * itself and takes the failed-read focus the moment it mounts, so the
   * panel never forces itself open or claims that focus -- even before the
   * picker mounts. Opened by hand, it still states the failure, with its
   * own retry control while no picker is mounted (BLOCK-001, AC-9).
   * Default `false`. */
  readonly pickerDeclared?: boolean;
  /** Shared, section-instance-wide claim (`useFailureFocusClaim`) so
   * exactly one retry control focuses itself when SEVERAL questions in
   * the same section fail at once — see that function's own doc comment. */
  readonly claimFailureFocus: () => boolean;
}

function renderItem(item: OnFileScreenItem, spec: SectionOnFileSpec, flagItems: readonly RepeatItemAnswer[]) {
  const heading = onFileItemHeading(item, spec.helpLine.labelAttributes);
  const flagWord =
    spec.soldSource !== undefined
      ? onFileItemFlagWord(item, spec.picker?.screenKey ?? spec.helpLine.screenKey, spec.soldSource.pickerFieldId, flagItems)
      : undefined;
  const otherAttributeLines = Object.entries(item.attributes)
    .filter(([name]) => !spec.helpLine.labelAttributes.includes(name))
    .flatMap(([name, value]) => {
      const text = onFileAttributeText(value);
      return text === undefined ? [] : [{ name, text }];
    });

  return (
    <Stack key={`${item.epicRecordId ?? "gap"}-${item.rowIndex}`} gap="none">
      <Typography variant="body">
        {heading !== "" ? heading : exposureContent.onFileItemFallbackLabel(item.rowIndex + 1)}
        {flagWord !== undefined ? (
          <Typography variant="body" as="span">
            {" "}
            <strong>{exposureContent.soldReplacedRemovedFlag(flagWord)}</strong>
          </Typography>
        ) : null}
      </Typography>
      {otherAttributeLines.map(({ name, text }) => (
        <Typography key={name} variant="helper" tone="muted">
          {humanizeFieldId(name)}: {text}
        </Typography>
      ))}
    </Stack>
  );
}

type OnFilePanelPartProps = Omit<OnFilePanelProps, "onToggle">;

/**
 * The panel's content plus its failed-read focus effect, shared by the
 * inline `OnFilePanel` and the split `OnFilePanelBody`. BLOCK-001: a failed
 * read always renders the failure wording -- never the nothing-on-file
 * wording, which only a SUCCEEDED read with no items may show (AC-9/AC-10)
 * -- with the retry control unless a mounted picker already renders one.
 */
function useOnFilePanelContent({
  id,
  spec,
  onFile,
  expanded,
  flagItems = [],
  retryOwnedElsewhere = false,
  pickerDeclared = false,
  claimFailureFocus,
}: Readonly<OnFilePanelPartProps>): { readonly content: ReactNode } {
  // `Button`'s closed prop surface forwards no ref (handles.md: queried by
  // role + accessible name, not a ref) -- a wrapper with a real DOM node
  // lets this effect find the real `<button>` element itself (a `.focus()`
  // call on anything else, e.g. a plain `<span>`, would silently do
  // nothing) without this feature component reaching into the design
  // system's internals.
  const retryWrapperRef = useRef<HTMLSpanElement | null>(null);
  const status = onFile?.state.status ?? "loading";
  // AC-4/SC 2.4.3: the retry control gets focus the moment this question's
  // own read becomes Failed and the panel is open. `ExposureQuestionsEngine`
  // opens the panel on that transition (it owns the expanded state, so the
  // disclosure toggle keeps working while the read stays failed -- the
  // customer can close it); focus waits for `expanded`, since a hidden
  // control cannot take it. The shared claim means a later re-open never
  // steals focus a second time.
  const forcedOpen = isOnFilePanelForcedOpen(onFile, pickerDeclared);
  useEffect(() => {
    if (forcedOpen && expanded && claimFailureFocus()) {
      retryWrapperRef.current?.querySelector("button")?.focus();
    }
  }, [forcedOpen, expanded, claimFailureFocus]);

  const screen = findOnFileScreen(onFile, spec.helpLine.screenKey, spec.helpLine.siblingScreenKeys);

  let content;
  if (status === "failed") {
    content = (
      <Stack gap="sm">
        <Typography variant="body">{exposureContent.onFileReadFailedMessage}</Typography>
        {retryOwnedElsewhere ? null : (
          <span ref={retryWrapperRef}>
            <Button emphasis="secondary" onClick={() => onFile?.onRetry()} testId={`${id}-retry`}>
              {exposureContent.onFileRetryLabel}
            </Button>
          </span>
        )}
      </Stack>
    );
  } else if (status === "loading") {
    content = (
      <Typography variant="helper" tone="muted">
        {pendingShellCopy.loadingLabel}
      </Typography>
    );
  } else if (screen === undefined || screen.itemCount === 0) {
    content = <Typography variant="body">{exposureContent.onFileNothingOnFile(spec.itemsLabel)}</Typography>;
  } else {
    content = <Stack gap="sm">{screen.items.map((item) => renderItem(item, spec, flagItems))}</Stack>;
  }

  return { content };
}

function triggerLabelFor(spec: SectionOnFileSpec): string {
  return spec.helpTriggerLabel ?? exposureContent.onFileHelpTriggerLabel(spec.itemsLabel);
}

/**
 * The data-on-file help panel (TG-5, AC-2/AC-3/AC-4). Renders through
 * `PortalPanel` (the 44×44 disclosure trigger, WCAG 2.2 AA SC 4.1.2),
 * trigger and content together -- for a question whose label is not a
 * `<legend>` (a currency question's text field).
 */
export function OnFilePanel(props: Readonly<OnFilePanelProps>) {
  const { content } = useOnFilePanelContent(props);
  return (
    <PortalPanel
      size="md"
      id={props.id}
      triggerLabel={triggerLabelFor(props.spec)}
      expanded={props.expanded}
      onToggle={props.onToggle}
    >
      {content}
    </PortalPanel>
  );
}

/**
 * REQ-006: the help panel's trigger alone, for a radio question's
 * `<legend>` -- `ExposureQuestionsEngine.tsx` places it directly after the
 * legend text, matching the approved H2 prototype's own `legendNode`
 * construction. Its content renders through `OnFilePanelBody` after the
 * legend instead, so the item list never joins the group's accessible
 * name. Pass both the same props.
 */
export function OnFilePanelTrigger({
  id,
  spec,
  expanded,
  onToggle,
}: Readonly<Pick<OnFilePanelProps, "id" | "spec" | "expanded" | "onToggle">>) {
  return <PortalPanel size="md" id={id} triggerLabel={triggerLabelFor(spec)} expanded={expanded} onToggle={onToggle} />;
}

/** REQ-006: `OnFilePanelTrigger`'s content, rendered after the legend. Its
 * element id is `onFilePanelBodyId(id)` (`on-file-render.ts`), which the
 * trigger's `aria-controls` names. */
export function OnFilePanelBody(props: Readonly<OnFilePanelPartProps>) {
  const { content } = useOnFilePanelContent(props);
  return (
    <PortalPanelBody id={props.id} expanded={props.expanded}>
      {content}
    </PortalPanelBody>
  );
}
